import { action, computed, makeObservable, observable, runInAction } from 'mobx'
import { getValue, isPending } from 'mobx-async'

import { IDate } from '@avantstay/arriere-clients/dist/arriereBackoffice'
import { Alert, capitalizeEachFirstLetter } from '@avantstay/backoffice-core'

import { MIN_SEARCH_TERM_LENGTH } from '@new/__global/components/OmniSearch/OmniSearch'
import { SearchPrefix } from '@new/__global/components/OmniSearch/OmniSearch.types'
import {
  parseSearchInput,
  sanitizeSearchTerm,
} from '@new/__global/components/OmniSearch/OmniSearch.utils'

import { trackError } from '@/tracking/customEventReport'
import {
  getOmniSearchBookings,
  getOmniSearchGuests,
  getOmniSearchHomeowners,
  getOmniSearchHomes,
} from '@arriere/modules/omniSearch'

const EMPTY_RESULT = { results: [], total: 0 }

const trackOmniSearch = ({
  searchTerm,
  searchType,
  error,
}: {
  searchType: string
  error: any
  searchTerm: string
}) => {
  Alert.Error(`${capitalizeEachFirstLetter(searchType)} search failed.`)
  trackError(`Omni Search: ${error}`, {
    searchType,
    error,
    searchTerm,
  })

  return EMPTY_RESULT
}

class _OmniSearchStore {
  showContainer: boolean = false

  inputValue: string = ''

  focusIn: SearchPrefix = SearchPrefix.ANY

  term: string = ''

  startDate?: IDate

  endDate?: IDate

  constructor() {
    makeObservable<
      _OmniSearchStore,
      | 'omniSearchResults'
      | 'omniSearchBookingsData'
      | 'omniSearchHomesData'
      | 'omniSearchGuestsData'
      | 'omniSearchHomeownersData'
    >(this, {
      showContainer: observable,
      inputValue: observable,
      focusIn: observable,
      term: observable,
      startDate: observable,
      endDate: observable,
      omniSearchResults: computed,
      omniSearchBookingsData: computed,
      omniSearchHomesData: computed,
      omniSearchGuestsData: computed,
      omniSearchHomeownersData: computed,
      isLoading: computed,
      bookingsSearchResults: computed,
      isLoadingBookingsResults: computed,
      homesSearchResults: computed,
      isLoadingHomesResults: computed,
      guestsSearchResults: computed,
      isLoadingGuestsResults: computed,
      homeownersSearchResults: computed,
      isLoadingHomeownersResults: computed,
      noResults: computed,
      setTerm: action.bound,
      setStartDate: action.bound,
      setEndDate: action.bound,
      reset: action.bound,
      setInputValue: action.bound,
      setShowContainer: action.bound,
    })
  }

  private get omniSearchBookingsData() {
    void this.term

    if (this.term?.length >= MIN_SEARCH_TERM_LENGTH) {
      const term = sanitizeSearchTerm(this.term)

      return getOmniSearchBookings({ term }).catch(error =>
        trackOmniSearch({
          searchType: 'bookings',
          error: error.message,
          searchTerm: term,
        }),
      )
    }

    return Promise.resolve(EMPTY_RESULT)
  }

  private get omniSearchHomesData() {
    void this.term

    if (this.term?.length >= MIN_SEARCH_TERM_LENGTH) {
      const term = sanitizeSearchTerm(this.term)

      return getOmniSearchHomes({ term }).catch(error =>
        trackOmniSearch({
          searchType: 'homes',
          error: error.message,
          searchTerm: term,
        }),
      )
    }

    return Promise.resolve(EMPTY_RESULT)
  }

  private get omniSearchGuestsData() {
    void [this.term, this.startDate, this.endDate]

    if (this.term?.length >= MIN_SEARCH_TERM_LENGTH) {
      const { startDate, endDate } = this
      const term = sanitizeSearchTerm(this.term)

      return getOmniSearchGuests({ term, endDate, startDate }).catch(error =>
        trackOmniSearch({
          searchType: 'guests',
          error: error.message,
          searchTerm: term,
        }),
      )
    }

    return Promise.resolve(EMPTY_RESULT)
  }

  private get omniSearchHomeownersData() {
    void this.term

    if (this.term?.length >= MIN_SEARCH_TERM_LENGTH) {
      const term = sanitizeSearchTerm(this.term)

      return getOmniSearchHomeowners({ term }).catch(error =>
        trackOmniSearch({
          searchType: 'homeowners',
          error: error.message,
          searchTerm: term,
        }),
      )
    }

    return Promise.resolve(EMPTY_RESULT)
  }

  get isLoading() {
    return (
      isPending(this.omniSearchBookingsData) ||
      isPending(this.omniSearchHomesData) ||
      isPending(this.omniSearchGuestsData) ||
      isPending(this.omniSearchHomeownersData)
    )
  }

  get noResults() {
    return (
      !this.isLoading &&
      this.bookingsSearchResults.total === 0 &&
      this.homesSearchResults.total === 0 &&
      this.guestsSearchResults.total === 0 &&
      this.homeownersSearchResults.total === 0
    )
  }

  get omniSearchResults() {
    return {
      bookings: this.bookingsSearchResults,
      homes: this.homesSearchResults,
      guests: this.guestsSearchResults,
      homeowners: this.homeownersSearchResults,
    }
  }

  get bookingsSearchResults() {
    return {
      isLoading: this.isLoadingBookingsResults,
      ...(getValue(this.omniSearchBookingsData) || { results: [], total: 0 }),
    }
  }

  get isLoadingBookingsResults() {
    return isPending(this.omniSearchBookingsData)
  }

  get homesSearchResults() {
    return {
      isLoading: this.isLoadingHomesResults,
      ...(getValue(this.omniSearchHomesData) || { results: [], total: 0 }),
    }
  }

  get isLoadingHomesResults() {
    return isPending(this.omniSearchHomesData)
  }

  get guestsSearchResults() {
    return {
      isLoading: this.isLoadingGuestsResults,
      ...(getValue(this.omniSearchGuestsData) || { results: [], total: 0 }),
    }
  }

  get isLoadingGuestsResults() {
    return isPending(this.omniSearchGuestsData)
  }

  get homeownersSearchResults() {
    return {
      isLoading: this.isLoadingHomeownersResults,
      ...(getValue(this.omniSearchHomeownersData) || { results: [], total: 0 }),
    }
  }

  get isLoadingHomeownersResults() {
    return isPending(this.omniSearchHomeownersData)
  }

  setTerm(term: string) {
    this.term = term
  }

  setStartDate(startDate?: string) {
    this.startDate = startDate
  }

  setEndDate(endDate?: string) {
    this.endDate = endDate
  }

  reset() {
    runInAction(() => {
      this.inputValue = ''
      this.focusIn = SearchPrefix.ANY
      this.term = ''
      this.startDate = undefined
      this.endDate = undefined
    })
  }

  setInputValue(value: string) {
    if (value.length === 0) {
      return this.reset()
    }

    this.inputValue = value
    const trimmed = value.trim()

    if (trimmed.length < 3) {
      return
    }

    const { focusIn, term, dates } = parseSearchInput(trimmed)

    runInAction(() => {
      this.focusIn = focusIn
      this.term = term
      this.startDate = dates?.startDate
      this.endDate = dates?.endDate
    })
  }

  setShowContainer(visible: boolean) {
    this.showContainer = visible
  }
}

export const OmniSearchStore = new _OmniSearchStore()
